import { useState, useRef, useCallback, useEffect } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { BRAND } from "@core/brand/brand-config";
// import { Tooltip } from "react-tooltip5";

type ComposerProps = {
  isStreaming: boolean;
  // A response is generating in the background (polling). Like streaming it locks
  // input, but there's no live stream to Stop — so we show a disabled send button.
  isAwaiting?: boolean;
  // May resolve false when the send was rejected (409) so we can restore the
  // user's typed text instead of losing it.
  onSend: (text: string) => void | Promise<boolean>;
  onStop: () => void;
  placeholder?: string;
  autoFocus?: boolean;
};

const MAX_HEIGHT_PX = 200;

export default function Composer({ isStreaming, isAwaiting = false, onSend, onStop, placeholder = `Ask ${BRAND.aiAssistantName}…`, autoFocus = false }: ComposerProps) {
  const [text, setText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isBusy = isStreaming || isAwaiting;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
    setOverflowing(el.scrollHeight > MAX_HEIGHT_PX);
  }, [text]);

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  }, []);

  const submit = useCallback(() => {
    const trimmed = text.trim();
    if (isBusy || !trimmed) return;
    // Clear optimistically for responsiveness; if the send was rejected (409),
    // put the text back so the user doesn't lose it (unless they've since typed
    // something new).
    setText("");
    Promise.resolve(onSend(trimmed)).then((accepted) => {
      if (accepted === false) setText((current) => current || trimmed);
    });
  }, [isBusy, text, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit]
  );

  const canSend = !isBusy && text.trim().length > 0;
  const effectivePlaceholder = isAwaiting ? "Generating response…" : placeholder;

  return (
    <div className="tw-relative tw-mx-auto tw-w-full">
      {/* Zeplin: Hermes_Landing — 84px field, 0.5px purple border with a 4px #f5f1fe ring */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-min-h-[84px] tw-box-border tw-bg-white tw-border-[0.5px] tw-border-solid tw-border-primary-purple-600 tw-rounded-xl tw-pl-4 tw-pr-[17px] tw-shadow-[0_0_0_4px_#f5f1fe]">
        <div className="tw-relative tw-flex-grow tw-min-w-0">
          <textarea ref={textareaRef} value={text} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} rows={1} disabled={isBusy} autoFocus={autoFocus} aria-label="Message input" className="tw-relative tw-z-10 tw-w-full tw-resize-none tw-border-none tw-outline-none tw-bg-transparent tw-text-base tw-text-gray-900 tw-placeholder-transparent tw-p-0 tw-my-[29.5px] tw-block tw-leading-6 tw-tracking-[-0.08px] disabled:tw-opacity-60 disabled:tw-cursor-not-allowed" style={{ maxHeight: `${MAX_HEIGHT_PX}px`, overflowY: overflowing ? "auto" : "hidden" }} />

          {!text && !isFocused && (
            <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-top-0 tw-overflow-hidden tw-py-[29.5px]">
              <span key={effectivePlaceholder} className="tw-block tw-truncate tw-text-base tw-text-gray-400 tw-leading-6 tw-tracking-[-0.08px] tw-animate-counter-roll">
                {effectivePlaceholder}
              </span>
            </div>
          )}
        </div>

        {isStreaming ? (
          <button onClick={onStop} aria-label="Stop generating" title="Stop generating" className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-bg-primary-purple-600 hover:tw-bg-primary-purple-700 tw-transition-colors tw-duration-150 tw-border-none tw-cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="tw-w-3.5 tw-h-3.5" aria-hidden="true">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          </button>
        ) : (
          <button onClick={submit} disabled={!canSend} aria-label="Send message" title="Send message" className={`tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-transition-colors tw-duration-150 tw-border-none ${canSend ? "tw-bg-primary-purple-600 hover:tw-bg-primary-purple-700 tw-cursor-pointer" : "tw-bg-primary-purple-100 tw-cursor-not-allowed"}`}>
            <span className="material-symbols-outlined !tw-text-[24px] tw-text-white" aria-hidden="true">
              arrow_right_alt
            </span>
          </button>
        )}
      </div>
      {/* <div className="tw-flex tw-gap-2 tw-my-4 tw-items-center tw-justify-center tw-text-gray-600">
        <div className="tw-font-medium tw-text-sm">Hermes capabilities</div>
        <div className="tw-flex-shrink-0 tw-border tw-rounded-full tw-border-gray-200 tw-px-2 tw-py-0.5 tw-text-xs" data-tooltip-id="hermes_capability" data-tooltip-content="Ask for global trade related information">
          Trade Intelligence
        </div>
        <div className="tw-flex-shrink-0 tw-border tw-rounded-full tw-border-gray-200 tw-px-2 tw-py-0.5 tw-text-xs" data-tooltip-id="hermes_capability" data-tooltip-content="Ask for product classification and applicable tariffs or duties">
          Tariff Intelligence
        </div>
      </div> */}
      {/* <Tooltip id="hermes_capability" place="top" className="trademo-tooltip !tw-opacity-100 !tw-text-xs" /> */}

      <p className="tw-absolute tw-top-full tw-inset-x-0 tw-m-0 tw-mt-3 tw-text-center tw-text-xs tw-text-gray-400">{BRAND.aiAssistantName} is an AI and can make mistakes. Please double-check responses.</p>
      {/*BRAND.key !== "source_atlas" && <p className="tw-text-center tw-text-xs tw-text-gray-400 tw-mb-2">* It is free for limited time. After that 2 value add credits will be deducted.</p>*/}
    </div>
  );
}
